import React, {useCallback, useEffect, useRef, useState} from "react";
import {PhotoLocation} from "../types/protocols";
import {useFirebaseImage} from "../Helpers/ImageLoader";
import COLORS from "../Helpers/utils";

interface PhotoWithMarkerProps {
    photoLocation: PhotoLocation;
    title?: string;
}

const PhotoWithMarker: React.FC<PhotoWithMarkerProps> = ({
    photoLocation,
    title = "Position i foto"
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const imageContainerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
    const { src, isLoading, hasError } = useFirebaseImage(photoLocation?.storagePath);
    
    // Zoom and pan state
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    
    // Calculate dynamic maximum zoom based on photo resolution
    const maxZoom = React.useMemo(() => {
        if (!photoLocation?.width || !photoLocation?.height || !containerDimensions.width) {
            return 5; // Default max zoom
        }
        
        // Calculate how much we're scaling down the photo to fit the container
        const scaleDownRatio = containerDimensions.width / photoLocation.width;
        
        // Allow zooming up to the original size, with a minimum of 2x and maximum of 10x
        const calculatedMax = Math.min(Math.max(1 / scaleDownRatio, 2), 10);
        
        return calculatedMax;
    }, [photoLocation?.width, photoLocation?.height, containerDimensions.width]);
    
    // Refs to access current values in event listeners
    const zoomRef = useRef(zoom);
    const panRef = useRef(pan);
    const maxZoomRef = useRef(maxZoom);
    
    useEffect(() => {
        zoomRef.current = zoom;
        panRef.current = pan;
        maxZoomRef.current = maxZoom;
    }, [zoom, pan, maxZoom]);

    // Handle mouse drag for panning
    const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
    const [hasMoved, setHasMoved] = useState(false);
    
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        // Don't start dragging if clicking on a marker
        if ((e.target as HTMLElement).closest('.marker')) {
            return;
        }
        
        setIsDragging(true);
        setHasMoved(false);
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
        setDragStartPos({ x: e.clientX, y: e.clientY });
    }, [pan]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (isDragging) {
            const moved = Math.abs(e.clientX - dragStartPos.x) > 3 || Math.abs(e.clientY - dragStartPos.y) > 3;
            if (moved) {
                setHasMoved(true);
            }
            
            setPan({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    }, [isDragging, dragStart, dragStartPos]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        setHasMoved(false);
    }, []);

    // Handle double-click to zoom in
    const handleDoubleClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        
        if (!containerRef.current) return;
        
        const currentZoom = zoomRef.current;
        const currentPan = panRef.current;
        const currentMaxZoom = maxZoomRef.current;
        
        // Multiplicative zoom: 50% increase on double-click
        const newZoom = Math.min(currentZoom * 1.5, currentMaxZoom);
        
        // Get mouse position relative to the container
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        // Calculate the point in the image that's under the mouse
        const pointX = (mouseX - currentPan.x) / currentZoom;
        const pointY = (mouseY - currentPan.y) / currentZoom;
        
        // Calculate new pan to keep that point under the mouse
        const newPan = {
            x: mouseX - pointX * newZoom,
            y: mouseY - pointY * newZoom
        };
        
        setZoom(newZoom);
        setPan(newPan);
    }, []);

    // Zoom control functions
    const handleZoomIn = () => {
        if (!containerRef.current) return;
        
        const currentZoom = zoomRef.current;
        const currentPan = panRef.current;
        const currentMaxZoom = maxZoomRef.current;
        // Multiplicative zoom: always 50% increase
        const newZoom = Math.min(currentZoom * 1.5, currentMaxZoom);
        
        // Zoom relative to center of container
        const rect = containerRef.current.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const pointX = (centerX - currentPan.x) / currentZoom;
        const pointY = (centerY - currentPan.y) / currentZoom;
        
        const newPan = {
            x: centerX - pointX * newZoom,
            y: centerY - pointY * newZoom
        };
        
        setZoom(newZoom);
        setPan(newPan);
    };

    const handleZoomOut = () => {
        if (!containerRef.current) return;
        
        const currentZoom = zoomRef.current;
        const currentPan = panRef.current;
        // Multiplicative zoom: always 33% decrease (inverse of 1.5x)
        const newZoom = Math.max(currentZoom / 1.5, 0.5);
        
        // Zoom relative to center of container
        const rect = containerRef.current.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const pointX = (centerX - currentPan.x) / currentZoom;
        const pointY = (centerY - currentPan.y) / currentZoom;
        
        const newPan = {
            x: centerX - pointX * newZoom,
            y: centerY - pointY * newZoom
        };
        
        setZoom(newZoom);
        setPan(newPan);
    };

    const handleResetZoom = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    const calculateMarkerPosition = useCallback(() => {
        if (!photoLocation || !containerDimensions.width) return null;

        const { x, y, width: photoWidth, height: photoHeight } = photoLocation;
        const { width: containerWidth } = containerDimensions;

        // Since the image uses width: '100%' and height: 'auto', 
        // it scales based on the container width only
        const scale = containerWidth / photoWidth;

        // Scale the marker position
        const scaledX = x * scale;
        const scaledY = y * scale;

        const markerSize = 35;
        return {
            left: `${scaledX}px`,
            top: `${scaledY}px`,
            width: `${markerSize}px`,
            height: `${markerSize}px`,
        };
    }, [photoLocation, containerDimensions]);

    // Update container dimensions
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                setContainerDimensions({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };

        const resizeObserver = new ResizeObserver(updateDimensions);

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        window.addEventListener('resize', updateDimensions);
        updateDimensions();

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateDimensions);
        };
    }, []);

    // Add native wheel event listener to handle zoom and prevent default scrolling
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !src) {
            return;
        }

        const handleNativeWheel = (e: WheelEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const currentZoom = zoomRef.current;
            const currentPan = panRef.current;
            const currentMaxZoom = maxZoomRef.current;
            
            // Multiplicative zoom for mouse wheel
            // deltaY is typically ~100 per wheel notch, so factor ~1.1 per notch
            const zoomFactor = Math.pow(1.1, -e.deltaY / 100);
            const newZoom = Math.min(Math.max(0.5, currentZoom * zoomFactor), currentMaxZoom);
            
            // Get mouse position relative to the container
            const rect = container.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            
            // Calculate the point in the image that's under the mouse
            const pointX = (mouseX - currentPan.x) / currentZoom;
            const pointY = (mouseY - currentPan.y) / currentZoom;
            
            // Calculate new pan to keep that point under the mouse
            const newPan = {
                x: mouseX - pointX * newZoom,
                y: mouseY - pointY * newZoom
            };
            
            setZoom(newZoom);
            setPan(newPan);
        };

        container.addEventListener('wheel', handleNativeWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleNativeWheel);
        };
    }, [src]);

    if (isLoading) {
        return <div>Laddar foto...</div>;
    }

    if (hasError || !src) {
        return <div>Foto saknas</div>;
    }

    const markerPosition = calculateMarkerPosition();

    return (
        <div>
            <h4 style={{marginBottom: '20px'}}>{title}</h4>

            <div
                ref={containerRef}
                className="photo-container"
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '900px',
                    margin: 0,
                    overflow: 'hidden',
                    borderRadius: '10px',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onDoubleClick={handleDoubleClick}
            >
                {/* Zoom Controls */}
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 1000,
                    display: 'flex',
                    gap: '5px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    padding: '5px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                }}>
                    <button
                        onClick={handleZoomIn}
                        disabled={zoom >= maxZoom}
                        style={{
                            width: '32px',
                            height: '32px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: zoom >= maxZoom ? '#f5f5f5' : 'white',
                            cursor: zoom >= maxZoom ? 'not-allowed' : 'pointer',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            color: zoom >= maxZoom ? '#ccc' : '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                        }}
                        title="Zooma in"
                    >
                        +
                    </button>
                    <button
                        onClick={handleZoomOut}
                        disabled={zoom <= 0.5}
                        style={{
                            width: '32px',
                            height: '32px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: zoom <= 0.5 ? '#f5f5f5' : 'white',
                            cursor: zoom <= 0.5 ? 'not-allowed' : 'pointer',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            color: zoom <= 0.5 ? '#ccc' : '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                        }}
                        title="Zooma ut"
                    >
                        −
                    </button>
                    <button
                        onClick={handleResetZoom}
                        disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                        style={{
                            width: '32px',
                            height: '32px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: (zoom === 1 && pan.x === 0 && pan.y === 0) ? '#f5f5f5' : 'white',
                            cursor: (zoom === 1 && pan.x === 0 && pan.y === 0) ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: (zoom === 1 && pan.x === 0 && pan.y === 0) ? '#ccc' : '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                        }}
                        title="Återställ"
                    >
                        ⟲
                    </button>
                </div>

                <div
                    ref={imageContainerRef}
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: '0 0',
                        transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                        position: 'relative',
                        width: '100%',
                        height: 'auto'
                    }}
                >
                    <img
                        src={src}
                        alt="Photo with marker"
                        className="photo-image"
                        onLoad={() => {
                            if (containerRef.current) {
                                setContainerDimensions({
                                    width: containerRef.current.offsetWidth,
                                    height: containerRef.current.offsetHeight
                                });
                            }
                        }}
                        style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            pointerEvents: 'none'
                        }}
                    />

                    {/* Render marker */}
                    {markerPosition && (
                        <div
                            className="marker"
                            style={{
                                ...markerPosition,
                                backgroundColor: COLORS.primary,
                                position: 'absolute',
                                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                                padding: '8px',
                                borderRadius: '50%',
                                border: '3px solid white',
                                transform: `translate(-50%, -50%) scale(${1 / zoom})`,
                                zIndex: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                willChange: 'transform',
                                pointerEvents: 'none'
                            }}
                        >
                            <div
                                style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: 'white',
                                    borderRadius: '50%'
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PhotoWithMarker;

